// had to do this, eslint was being dumb and complaining about hooks being called in a conditional
/* eslint-disable react-hooks/rules-of-hooks */
import { FC, useState, useContext, useMemo, useCallback } from 'react';
import { Cancel, Close, Info, Save } from '@mui/icons-material';
import {
  Grid,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  FormControl,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Switch,
  IconButton,
  useMediaQuery,
  Link,
  MenuItem,
  Stack,
  Alert
} from '@mui/material';
import { Form, useFormik, FormikProvider } from 'formik';
import { useHistory, Link as RouterLink } from 'react-router-dom';
import { string, mixed, array, object, boolean, number } from 'yup';
import { useSnackbar } from 'notistack';
import {
  JobTitlesDropdown,
  Loader,
  SelectAsync,
  SMBSelect,
  StatesDropdown
} from '../../../../components';
import {
  createUser,
  getAllAppRoles,
  getPlaceOrderAvailableReportsTo,
  getAppRolesByJobTitle,
  createPeopleAgentUpload,
  createPeopleLenderUpload,
  updateUser,
  getUserByEmail,
  getPlaceOrderUsers,
  getUserJobTitles,
  getSageEmployees
} from '../../../../fetch';
import {
  IDropdownResponse,
  IManagePlaceOrderReportsTo,
  ExternalUserRoles,
  IAdminUser,
  IResponse,
  IManagePlaceOrderUser,
  IJobTitle,
  ISageEmployee
} from '../../../../models';
import { UserContext } from '../../../../context';
import { useQuery } from 'react-query';
import { AgentFields } from '../../manage-admin-users/agent-fields';
import { alphaSort, convertToNumber, formatInputPhoneNumber, urlRegex } from '../../../../helpers';
import { LenderFields } from '../../manage-admin-users/lender-fields';
import { LenderProcessorFields } from '../../manage-admin-users/lender-processor-fields';
import { VendorAffiliateFields } from '../../manage-admin-users/vendor-affiliate-fields';
import {
  getAdminLookupAttorneyOffices,
  getAdminLookupNeighborhoods,
  getAdminLookupTerritories
} from '../../../../fetch/lookups';
import { ClientFields } from '../../manage-admin-users/client-fields';
import { UserAppRolesDescriptionDrawer } from './user-app-roles-description-drawer';
import { useDebounce } from '../../../../hooks';

interface IUserDetails {
  isLoadingUserDetails?: boolean;
  userId: string;
  currentUser?: IAdminUser;
}

const InternalAgentJobTitle = 'Internal Agent';

const UserDetailsSchema = (isCreating: boolean, externalUser: boolean) =>
  object().shape({
    canImpersonateInternalUser: boolean(),
    canImpersonateExternalUser: boolean(),
    firstName: string().required('First name is required'),
    lastName: string().required('Last name is required'),
    email: string().email('Email is not valid').required('Required'),
    jobTitle: string().required('Required'),
    isEmployee: string().required('Required'),
    requiresPlaceOrderReportsTo: boolean().nullable(),
    allowedAccountNames: array().of(string()),
    reportsTo: mixed().when(['requiresPlaceOrderReportsTo'], {
      is: requiresPlaceOrderReportsTo => requiresPlaceOrderReportsTo && !isCreating,
      then: string().required('Required'),
      otherwise: string().notRequired()
    }),
    roleIds: array().min(1, 'Required'),
    phoneNumber: mixed().when('jobTitle', {
      is: val =>
        (val === ExternalUserRoles.VendorAffiliate ||
          val === ExternalUserRoles.Purchaser ||
          val === ExternalUserRoles.InternalAgent) &&
        !isCreating,
      then: string().max(255, 'Max 255 characters').required('Required'),
      otherwise: string().notRequired()
    }),
    address: string(),
    address2: string(),
    city: string(),
    state: string(),
    zip: string(),
    isActive: boolean(),
    // attorneys
    attorneyOfficeId: mixed().when('jobTitle', {
      is: val => val === ExternalUserRoles.Attorney && !isCreating,
      then: string().required('Required'),
      otherwise: string().notRequired()
    }),
    // agents
    agency: string(),
    cellPhoneNumber: string().max(255, 'Max 255 characters'),
    webSite: string().matches(urlRegex, 'URL is not valid'),
    includeAgentNameInUrl: boolean(),
    agentNeighborhoodIds: array(),
    uploadPersonalFile: mixed(),
    uploadLogoFile: mixed(),
    // customs specific
    workplace: string().when('jobTitle', {
      is: val =>
        (val === ExternalUserRoles.Custom ||
          val === InternalAgentJobTitle ||
          val === ExternalUserRoles.Lender) &&
        !isCreating,
      then: string().required('Required'),
      otherwise: string().notRequired()
    }),
    // clients
    agentId: string()
      .nullable()
      .when('jobTitle', {
        is: jobTitle => jobTitle === ExternalUserRoles.Purchaser && !isCreating,
        then: string().required('Required')
      }),
    hideBuilds: boolean(),
    contractName: string(),
    buyerHasAgent: boolean(),
    agentFirstName: string(),
    agentLastName: string(),
    agentEmail: string(),
    agentPhone: string(),
    agentSellingCompany: string(),
    // vendors
    partners: mixed().when('jobTitle', {
      is: jobTitle => jobTitle === ExternalUserRoles.VendorAffiliate && !isCreating,
      then: array().of(
        object()
          .shape({
            partnerId: string().required('Required'),
            roles: array().of(string().required('Required')).min(1, 'Required').required('Required')
          })
          .required('Required')
      ),
      otherwise: array().notRequired()
    }),
    assignedLenders: mixed().when('jobTitle', {
      is: jobTitle => jobTitle === ExternalUserRoles.LenderProcessor && !isCreating,
      then: array().of(string().required('Required')).min(1, 'At least one required'),
      otherwise: array().of(string().notRequired()).notRequired()
    }),
    territoryIds: array(),
    neighborhoodIds: array(),
    sageEmployeeId: number()
  });

export const UserDetails: FC<IUserDetails> = ({ isLoadingUserDetails, userId, currentUser }) => {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { isGlobalAdmin } = useContext(UserContext);
  const [appRolesInputValue, setAppRolesInputValue] = useState<string>('');
  const [territoryInputValue, setTerritoryInputValue] = useState<string>('');
  const [neighborhoodInputValue, setNeighborhoodInputValue] = useState<string>('');
  const [selectedAppRoles, setSelectedAppRoles] = useState<IDropdownResponse[]>([]);
  const [selectedTerritoryIds, setSelectedTerritoryIds] = useState<IDropdownResponse[]>([]);
  const [selectedNeighborhoodIds, setSelectedNeighborhoodIds] = useState<IDropdownResponse[]>([]);
  const [showAppRolesInfo, setShowAppRolesInfo] = useState(false);
  const [existingUserId, setExistingUserId] = useState<number | null>(null);
  const [personId, setPersonId] = useState<number | null>(null);
  const [accountIsDisabled, setAccountIsDisabled] = useState<boolean>(false);
  const [employeeDomainValid, setEmployeeDomainValid] = useState<boolean>(true);
  const [sageEmployees, setSageEmployees] = useState<ISageEmployee[]>([]);

  const isCreating = userId === 'add-new-user';
  const workplaceMap = {
    [ExternalUserRoles.InternalAgent]: currentUser?.externalUser?.agent?.workplace ?? '',
    [ExternalUserRoles.Custom]: currentUser?.externalUser?.custom?.workplace ?? '',
    [ExternalUserRoles.Lender]: currentUser?.externalUser?.lender?.workplace ?? '',
    [ExternalUserRoles.LenderProcessor]: currentUser?.externalUser?.lenderProcessor?.workplace ?? ''
  };
  const territoriesMap = {
    [ExternalUserRoles.LenderProcessor]:
      currentUser?.externalUser?.lenderProcessor?.lenderTerritoryIds ?? '',
    [ExternalUserRoles.Lender]: currentUser?.externalUser?.lender?.lenderTerritoryIds ?? ''
  };
  const lenderStatusMap = {
    [ExternalUserRoles.Lender]: currentUser?.externalUser?.lender?.lenderStatus ?? '',
    [ExternalUserRoles.LenderProcessor]:
      currentUser?.externalUser?.lenderProcessor?.lenderStatus ?? ''
  };

  const workplace = workplaceMap[currentUser?.jobTitlePersonType] ?? '';
  const territories = territoriesMap[currentUser?.jobTitle] ?? [];
  const lenderStatus = lenderStatusMap[currentUser?.jobTitle] ?? 'NotApproved';

  const getPartners = () => {
    if (currentUser?.externalUser?.vendor?.partners.length > 0) {
      return currentUser?.externalUser?.vendor?.partners?.map(part => ({
        partnerId: part.partnerId,
        roles: part.roles
      }));
    } else {
      return [{ partnerId: '', roles: [] }];
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      canImpersonateInternalUser: currentUser?.internalUser?.canImpersonateInternalUser ?? false,
      canImpersonateExternalUser: currentUser?.internalUser?.canImpersonateExternalUser ?? false,
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      email: currentUser?.email ?? '',
      isEmployee: currentUser?.externalUser ? 'No' : 'Yes',
      wasInternalUser: currentUser?.internalUser ? true : false, // used to determine if user was internal on page load and later switched to Agent (an external job title)
      jobTitle: currentUser?.jobTitle,
      jobTitlePersonType: currentUser?.jobTitlePersonType,
      roleIds: currentUser?.roleIds ?? [],
      reportsTo: currentUser?.internalUser?.placeOrderReportsTo ?? '',
      isActive: currentUser?.externalUser?.isActive ?? true,
      allowedAccountNames: currentUser?.internalUser?.allowedAccountNames ?? [],
      // used only in form not in api
      requiresPlaceOrderReportsTo: currentUser?.requiresPlaceOrderReportsTo,
      phoneNumber: currentUser?.externalUser?.phoneNumber ?? '',
      address: currentUser?.externalUser?.address ?? '',
      address2: currentUser?.externalUser?.address2 ?? '',
      city: currentUser?.externalUser?.city ?? '',
      state: currentUser?.externalUser?.state ?? '',
      zip: currentUser?.externalUser?.zip ?? '',
      fax: currentUser?.externalUser?.faxNumber ?? '',
      // attorneys
      attorneyOfficeId: currentUser?.externalUser?.attorney?.attorneyOfficeId ?? '',
      // agent fields
      cellPhoneNumber: currentUser?.externalUser?.cellPhoneNumber ?? '',
      isSalesAdmin: false,
      isSalesCoordinator: false,
      webSite: currentUser?.externalUser?.agent?.webSite ?? '',
      includeAgentNameInUrl: currentUser?.externalUser?.agent?.includeAgentNameInUrl ?? false,
      agentNeighborhoodIds: currentUser?.externalUser?.agent?.neighborhoodIds ?? [],
      // lender specific
      workplace: workplace,
      lenderStatus: lenderStatus,
      lenderTerritoryIds: territories,
      // lender processor specific
      assignedLenders: currentUser?.externalUser?.lenderProcessor?.assignedLenders ?? [],
      // vendor/trade partner specific
      partners: getPartners(),
      // customs specific
      roles: [],
      uploadPersonalFile: '',
      uploadLogoFile: '',
      // clients
      agentId: currentUser?.externalUser?.purchaser?.agentId ?? '',
      contractName: currentUser?.externalUser?.purchaser?.contractName ?? '',
      buyerHasAgent: currentUser?.externalUser?.purchaser?.buyerHasAgent ?? false,
      agentFirstName: currentUser?.externalUser?.purchaser?.agentFirstName ?? '',
      agentLastName: currentUser?.externalUser?.purchaser?.agentLastName ?? '',
      agentEmail: currentUser?.externalUser?.purchaser?.agentEmail ?? '',
      agentPhone: currentUser?.externalUser?.purchaser?.agentPhone ?? '',
      agentSellingCompany: currentUser?.externalUser?.purchaser?.agentSellingCompany ?? '',
      additionalContacts: currentUser?.externalUser?.purchaser?.additionalContacts ?? [],
      hideBuilds: currentUser?.externalUser?.purchaser?.hideBuilds ?? false,
      // internal users
      territoryIds: currentUser?.internalUser?.territories?.map(t => t.territoryId) ?? [],
      neighborhoodIds: currentUser?.internalUser?.neighborhoods?.map(n => n.neighborhoodId) ?? [],
      sageEmployeeId: currentUser?.internalUser?.sageEmployeeRecnum ?? ''
    },
    validationSchema: UserDetailsSchema(isCreating, !!currentUser?.externalUser),
    onSubmit: async (values, actions) => {
      try {
        if (isCreating) {
          const createData = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            jobTitle: values.jobTitle,
            roleIds: values.roleIds
          };
          await createUser(createData);
        } else {
          const updateData = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            jobTitle: values.jobTitle,
            roleIds: values.roleIds,
            internalUser:
              values.isEmployee === 'Yes'
                ? {
                    reportsTo: values?.reportsTo ?? null,
                    territoryIds: values.territoryIds ?? [],
                    neighborhoodIds: values.neighborhoodIds ?? [],
                    canImpersonateExternalUser: values.canImpersonateExternalUser,
                    canImpersonateInternalUser: values.canImpersonateInternalUser,
                    allowedAccountNames: values?.allowedAccountNames ?? [],
                    sageEmployeeId: values?.sageEmployeeId ?? ''
                  }
                : {},
            externalUser:
              values.isEmployee === 'No'
                ? {
                    phoneNumber: values.phoneNumber,
                    cellPhoneNumber: values.cellPhoneNumber,
                    faxNumber: values.fax,
                    address: values.address,
                    address2: values.address2,
                    city: values.city,
                    state: values.state,
                    zip: values.zip,
                    isActive: values.isActive,
                    attorney:
                      values.jobTitlePersonType === ExternalUserRoles.Attorney
                        ? {
                            attorneyOfficeId: convertToNumber(values.attorneyOfficeId)
                          }
                        : undefined,
                    agent:
                      values.jobTitlePersonType === ExternalUserRoles.InternalAgent
                        ? {
                            workplace: values.workplace,
                            webSite: values.webSite,
                            includeAgentNameInUrl: values.includeAgentNameInUrl,
                            neighborhoodIds: values.agentNeighborhoodIds
                          }
                        : undefined,
                    purchaser:
                      values.jobTitlePersonType === ExternalUserRoles.Purchaser
                        ? {
                            agentId: convertToNumber(values.agentId),
                            contractName: values.contractName,
                            buyerHasAgent: values.buyerHasAgent,
                            externalAgentId:
                              values.buyerHasAgent && currentUser
                                ? currentUser?.externalUser?.purchaser?.externalAgentId
                                : null,
                            agentFirstName: values.agentFirstName,
                            agentLastName: values.agentLastName,
                            agentEmail: values.agentEmail,
                            agentPhone: values.agentPhone,
                            agentSellingCompany: values.agentSellingCompany,
                            hideBuilds: values.hideBuilds
                          }
                        : undefined,
                    vendor:
                      values.jobTitlePersonType === ExternalUserRoles.VendorAffiliate
                        ? {
                            partners: values.partners.map(p => ({
                              partnerId: convertToNumber(p.partnerId),
                              roles: p.roles
                            }))
                          }
                        : undefined,
                    lender:
                      values.jobTitlePersonType === ExternalUserRoles.Lender
                        ? {
                            workplace: values.workplace,
                            lenderStatus: values.lenderStatus,
                            lenderTerritoryIds: values.lenderTerritoryIds
                          }
                        : undefined,
                    lenderProcessor:
                      values.jobTitlePersonType === ExternalUserRoles.LenderProcessor
                        ? {
                            workplace: values.workplace,
                            lenderStatus: values.lenderStatus,
                            lenderTerritoryIds: values.lenderTerritoryIds,
                            assignedLenders: values.assignedLenders
                          }
                        : undefined,
                    custom:
                      values.jobTitlePersonType === ExternalUserRoles.Custom
                        ? {
                            workplace: values.workplace
                          }
                        : undefined
                  }
                : undefined
          };
          await updateUser(currentUser.userId, updateData);
          // create personal and logo uploads for external user agents
          if (values.jobTitlePersonType === ExternalUserRoles.InternalAgent) {
            if (values.uploadPersonalFile) {
              const formData = new FormData();
              formData.append('Image', values.uploadPersonalFile);
              await createPeopleAgentUpload(currentUser.personId, 'personal', formData);
            }

            if (values.uploadLogoFile) {
              const formData = new FormData();
              formData.append('Image', values.uploadLogoFile);
              await createPeopleAgentUpload(currentUser.personId, 'logo', formData);
            }
          } else if (
            values.jobTitlePersonType === ExternalUserRoles.LenderProcessor ||
            values.jobTitlePersonType === ExternalUserRoles.Lender
          ) {
            if (values.uploadPersonalFile) {
              const formData = new FormData();
              formData.append('Image', values.uploadPersonalFile);
              await createPeopleLenderUpload(currentUser.personId, 'personal', formData);
            }

            if (values.uploadLogoFile) {
              const formData = new FormData();
              formData.append('Image', values.uploadLogoFile);
              await createPeopleLenderUpload(currentUser.personId, 'logo', formData);
            }
          }
        }
        enqueueSnackbar('User saved!', {
          variant: 'success'
        });
        actions.resetForm();
        history.push('/admin/user-tools/unified-user-management');
      } catch (error) {
        const err = isCreating ? 'Error creating user.' : 'Error updating user.';
        enqueueSnackbar(error?.Detail ?? err, {
          variant: 'error'
        });
      }
    }
  });
  const {
    handleSubmit,
    handleBlur,
    setFieldValue,
    values,
    isSubmitting,
    errors,
    touched,
    dirty,
    isValid,
    initialValues,
    validateField
  } = formik;

  const { data: jobTitlesData } = useQuery<IJobTitle[], Error>(
    ['job-titles'],
    () => getUserJobTitles(),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const currentJobTitleConfig = useMemo(
    () => jobTitlesData?.find(jt => jt.value === values.jobTitle),
    [jobTitlesData, values.jobTitle]
  );

  const hasJobTitleWithTerritories = useMemo(
    () => currentJobTitleConfig?.allowsTerritoryConfiguration ?? false,
    [currentJobTitleConfig]
  );

  const hasJobTitleWithNeighborhoods = useMemo(
    () => currentJobTitleConfig?.allowsNeighborhoodConfiguration ?? false,
    [currentJobTitleConfig]
  );

  const { isLoading: isLoadingNeighborhoods, data: neighborhoodsData } = useQuery<
    IDropdownResponse[],
    Error
  >(
    ['neighborhoods-filters', hasJobTitleWithNeighborhoods],
    () => getAdminLookupNeighborhoods(null, true),
    {
      notifyOnChangeProps: 'tracked',
      enabled: hasJobTitleWithNeighborhoods,
      onSuccess: d => {
        setSelectedNeighborhoodIds(
          d.filter(r => values.neighborhoodIds.includes(convertToNumber(r.value)))
        );
      }
    }
  );

  // Load Sage employees only if the user is being added
  const { isLoading: isLoadingSageEmployees } = useQuery<ISageEmployee[], Error>(
    'get-sage-employees',
    () => getSageEmployees(),
    {
      enabled: values.isEmployee === 'Yes',
      onSuccess: data => {
        if (currentUser?.internalUser?.sageEmployeeRecnum) {
          data.push({
            value: currentUser?.internalUser?.sageEmployeeRecnum,
            description: currentUser?.internalUser?.sageEmployeeDisplayInfo,
            shorthand: null
          });
        }

        setSageEmployees(alphaSort(data, 'description'));
      }
    }
  );

  const { isLoading: isLoadingSelectedWhoCanView, data: whoCanView } = useQuery<
    IResponse<IManagePlaceOrderUser[]>,
    Error
  >(['getPlaceOrderUsers'], () => getPlaceOrderUsers({ page: null }), {
    enabled: values.requiresPlaceOrderReportsTo
  });

  const { isLoading: isLoadingTerritories, data: territoriesData } = useQuery<
    IDropdownResponse[],
    Error
  >(['territories-filters', hasJobTitleWithTerritories], () => getAdminLookupTerritories(), {
    notifyOnChangeProps: 'tracked',
    enabled: hasJobTitleWithTerritories,
    onSuccess: d => {
      setSelectedTerritoryIds(
        d.filter(r => values.territoryIds.includes(convertToNumber(r.value)))
      );
    }
  });

  const { isLoading: isLoadingAppRoles, data: appRoles } = useQuery<IDropdownResponse[], Error>(
    ['all-app-roles'],
    () => getAllAppRoles(),
    {
      onSuccess: d => {
        setSelectedAppRoles(d.filter(r => values.roleIds.includes(r.value)));
      },
      onError: (err: any) => {
        enqueueSnackbar(err?.Detail ?? `Error fetching app roles.`, {
          variant: 'error'
        });
      }
    }
  );

  const { isLoading: isLoadingJobTitleRoles } = useQuery<string[], Error>(
    ['job-title-roles', values.jobTitle, appRoles],
    () => getAppRolesByJobTitle(values.jobTitle),
    {
      notifyOnChangeProps: 'tracked',
      onSuccess: d => {
        const selected = appRoles?.filter(r => d.includes(r.value));
        const currentRoles = currentUser
          ? appRoles?.filter(r => currentUser?.roleIds.includes(r.value))
          : [];
        if (isCreating) {
          setSelectedAppRoles(selected ?? []);
          setFieldValue(
            'roleIds',
            selected.map(v => v.value)
          );
          // set when the jobtitle changes
        } else if (values.jobTitle !== currentUser?.jobTitle) {
          // concat the current users roles with selected ones and filter out duplicates
          const cleanAppRoles = [...currentRoles, ...(selected ?? [])].filter(
            (v, i, arr) => arr.indexOf(v) === i
          );
          setSelectedAppRoles(cleanAppRoles);
          setFieldValue(
            'roleIds',
            cleanAppRoles.map(v => v.value)
          );
        }
      },
      enabled: !!values.jobTitle && !!appRoles,
      onError: (err: any) => {
        enqueueSnackbar(err?.Detail ?? `Error pre-populating roles based on job title.`, {
          variant: 'error'
        });
      }
    }
  );

  const checkIfUserExistsCallback = useCallback(async () => {
    if (!formik.values.email.includes('@')) {
      setExistingUserId(null);
      setPersonId(null);
      setEmployeeDomainValid(true);
      return;
    }

    if (isCreating && values.isEmployee === 'Yes') {
      const domain = values.email.split('@')[1]?.toLowerCase() || '';
      const allowedDomains = ['stonemartin.com', 'stonemartinbuilders.com'];
      if (!allowedDomains.includes(domain)) {
        setExistingUserId(null);
        setPersonId(null);
        setEmployeeDomainValid(false);
        return;
      }
    }
    try {
      const result = await getUserByEmail(formik.values.email);
      if (result?.userId) {
        setExistingUserId(result.userId);
        setAccountIsDisabled(result.accountIsDisabled);
      } else if (result?.personId) {
        setPersonId(result.personId);
        setAccountIsDisabled(result.accountIsDisabled);
      } else {
        setExistingUserId(null);
        setPersonId(null);
      }
    } catch (error) {
      setExistingUserId(null);
      setPersonId(null);
      enqueueSnackbar('Error searching for duplicate record.', {
        variant: 'error'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.email, formik.values.isEmployee, isCreating, enqueueSnackbar]);

  useDebounce(checkIfUserExistsCallback, 500, [formik.values.email, formik.values.isEmployee]);

  const isPhoneNumberRequired = useMemo(() => {
    return (
      values.jobTitlePersonType === ExternalUserRoles.VendorAffiliate ||
      values.jobTitlePersonType === ExternalUserRoles.Purchaser ||
      (values.jobTitlePersonType === ExternalUserRoles.InternalAgent && !isCreating)
    );
  }, [values.jobTitlePersonType, isCreating]);

  return (
    <>
      {isLoadingUserDetails && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingUserDetails && (
        <FormikProvider value={formik}>
          {isSubmitting && (
            <Loader type="fullscreen" position="centered">
              Saving...
            </Loader>
          )}
          <Form onSubmit={handleSubmit} noValidate>
            <Box>
              <Grid container spacing={1}>
                <Grid item xs={12} md={showAppRolesInfo ? 9 : 12}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        size="small"
                        required
                        disabled={!isCreating}
                      >
                        <Typography>Is this user an employee?*</Typography>
                        <RadioGroup
                          value={values.isEmployee}
                          onChange={e => {
                            setFieldValue('isEmployee', e.target.value);
                            setFieldValue('jobTitle', '');
                            if (e.target.value === 'No') {
                              setEmployeeDomainValid(true);
                            }
                          }}
                          aria-label="isEmployee"
                          name="isEmployee"
                          row
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel
                            value="No"
                            control={<Radio />}
                            label="Non-employee / guest / other"
                          />
                        </RadioGroup>
                      </FormControl>
                      {!isCreating && isGlobalAdmin && currentUser?.internalUser && (
                        <>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.canImpersonateInternalUser}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                  checked: boolean
                                ) => {
                                  setFieldValue('canImpersonateInternalUser', checked);
                                }}
                                name="canImpersonateInternalUser"
                                color="primary"
                              />
                            }
                            label="Can Impersonate Internal Roles"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.canImpersonateExternalUser}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                  checked: boolean
                                ) => {
                                  setFieldValue('canImpersonateExternalUser', checked);
                                }}
                                name="canImpersonateExternalUser"
                                color="primary"
                              />
                            }
                            label="Can Impersonate External Roles"
                          />
                        </>
                      )}
                    </Grid>
                    <Grid item xs={6} sm={'auto'}>
                      {currentUser?.externalUser && !currentUser?.externalUser?.isActive && (
                        // Only display toggle when external user is inactive, used for reactivating user
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              checked={values.isActive}
                              id="active-inactive"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                                checked: boolean
                              ) => {
                                setFieldValue('isActive', checked);
                              }}
                            />
                          }
                          label="Active"
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <JobTitlesDropdown
                        value={values.jobTitle?.toString() ?? ''}
                        handleChange={(value, jobTitle) => {
                          setFieldValue('jobTitle', value);

                          // the form could get in weird validation state where it thinks jobTitle is still required even after selecting
                          // an option from the dropdown, so a double check here to run the validation
                          setTimeout(() => {
                            validateField('jobTitle');
                          }, 50);

                          setFieldValue('jobTitlePersonType',jobTitle.personTypeName)

                          if (jobTitle.isExternal) {
                            setFieldValue('isEmployee', 'No');
                          }
                          // user was internal, switched to external, and now switched back to internal
                          else if (values.wasInternalUser && values.isEmployee === 'No') {
                            setFieldValue('isEmployee', 'Yes');
                          }

                          if (jobTitle?.requiresPlaceOrderReportsTo) {
                            setFieldValue(
                              'requiresPlaceOrderReportsTo',
                              jobTitle.requiresPlaceOrderReportsTo
                            );
                          } else {
                            setFieldValue('requiresPlaceOrderReportsTo', false);
                            setFieldValue('reportsTo', '');
                            // set a slight delay here to validate this field so you see the form updates right away and you don't have to
                            // click on another field to trigger form validation. Otherwise the form still thinks reportsTo is required
                            // this happens if you go to a user who already has a reportsTo selected and then change their job title to something
                            // that doesn't require a reportsTo
                            setTimeout(() => {
                              validateField('reportsTo');
                            }, 50);
                          }
                        }}
                        isExternal={
                          values.isEmployee === 'No' && !values.wasInternalUser ? true : false
                        }
                        isDisabled={
                          !isCreating && values.isEmployee === 'No' && !values.wasInternalUser
                        }
                        isRequired
                      />
                      {!isCreating &&
                        !currentUser?.externalUser &&
                        values.requiresPlaceOrderReportsTo &&
                        !!values.email && (
                          <Stack mt={1} gap={1}>
                            <SelectAsync
                              label="Reports to"
                              onClear={() => {
                                setFieldValue('reportsTo', '');
                              }}
                              name="reportsTo"
                              hasClear
                              required
                              apiRequest={() =>
                                // grab the first part of the email to try and match reports to options otherwise it will show everything
                                getPlaceOrderAvailableReportsTo(values.email.split('@')[0])
                              }
                              transformResponse={(res: IManagePlaceOrderReportsTo[]) =>
                                res.map(r => ({
                                  label: r.displayName,
                                  value: r.accountName
                                }))
                              }
                            />
                            <SMBSelect
                              variant="outlined"
                              label="Other Users Who Can View My Data"
                              value={values.allowedAccountNames}
                              onChange={event => {
                                setFieldValue(
                                  'allowedAccountNames',
                                  typeof event.target.value === 'string'
                                    ? event.target.value.split(',')
                                    : event.target.value
                                );
                              }}
                              SelectProps={{
                                multiple: true
                              }}
                              handleClear={() => {
                                setFieldValue('allowedAccountNames', []);
                              }}
                              disabled={isLoadingSelectedWhoCanView}
                            >
                              {whoCanView &&
                                whoCanView?.records?.map((whoCanView, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={whoCanView.accountName}>
                                      {whoCanView.displayName}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Stack>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={0.25}>
                        <Autocomplete
                          multiple
                          disabled={isLoadingAppRoles || isLoadingJobTitleRoles || !values.jobTitle}
                          loading={isLoadingJobTitleRoles}
                          id="appRoles"
                          value={alphaSort(selectedAppRoles, 'description')}
                          onChange={(event, newValue: any[]) => {
                            setSelectedAppRoles(newValue);
                            setFieldValue(
                              'roleIds',
                              newValue.map(v => v.value)
                            );
                          }}
                          fullWidth
                          inputValue={appRolesInputValue}
                          onInputChange={(event, newInputValue) => {
                            setAppRolesInputValue(newInputValue);
                          }}
                          options={appRoles ?? []}
                          getOptionLabel={option => option?.description}
                          filterSelectedOptions
                          renderOption={(props, option: IDropdownResponse) => {
                            return (
                              <li {...props} key={`${option?.value}`}>
                                {option?.description}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              name="appRoles"
                              size="small"
                              disabled={isLoadingAppRoles}
                              required
                              label={isLoadingAppRoles ? 'Loading...' : 'App Roles'}
                              placeholder="Add App Role"
                            />
                          )}
                        />
                        <IconButton
                          color="primary"
                          onClick={() => setShowAppRolesInfo(!showAppRolesInfo)}
                        >
                          {showAppRolesInfo ? <Cancel /> : <Info />}
                        </IconButton>
                      </Box>
                    </Grid>
                    {isMobile && showAppRolesInfo && <UserAppRolesDescriptionDrawer />}
                    {currentUser?.isMember && !isCreating && (
                      <Grid item xs={12}>
                        <Alert severity="info">
                          To request an edit to the First Name, Last Name, or Email, please contact
                          SMB IT.
                        </Alert>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        autoComplete="nope"
                        label="First Name"
                        name="firstName"
                        size="small"
                        value={values.firstName}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('firstName', e.target.value)}
                        error={Boolean(touched.firstName && errors.firstName)}
                        helperText={touched.firstName && errors.firstName ? errors.firstName : ''}
                        disabled={currentUser?.isMember && !isCreating}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        autoComplete="nope"
                        label="Last Name"
                        name="lastName"
                        size="small"
                        value={values.lastName}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('lastName', e.target.value)}
                        error={Boolean(touched.lastName && errors.lastName)}
                        helperText={touched.lastName && errors.lastName ? errors.lastName : ''}
                        disabled={currentUser?.isMember && !isCreating}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        autoComplete="nope"
                        label="Email"
                        name="email"
                        size="small"
                        value={values.email}
                        onBlur={handleBlur}
                        onChange={e => {
                          setFieldValue('email', e.target.value);
                          setExistingUserId(null);
                          setPersonId(null);
                          setEmployeeDomainValid(true);
                        }}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email ? errors.email : ''}
                        disabled={currentUser?.isMember && !isCreating}
                      />
                      {isCreating && !employeeDomainValid && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          Employee users must have a Stone Martin email address.
                        </Typography>
                      )}
                      {existingUserId &&
                        !accountIsDisabled &&
                        currentUser?.email !== values.email && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            This user already exists.{' '}
                            <Link
                              component={RouterLink}
                              to={`/admin/user-tools/unified-user-management/${existingUserId}`}
                              sx={{
                                color: theme => theme.palette.primary.main,
                                textDecoration: 'underline'
                              }}
                            >
                              Click here
                            </Link>{' '}
                            to access their details.
                          </Typography>
                        )}
                      {personId && !accountIsDisabled && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          This user has not yet been invited to Entra.{' '}
                          <Link
                            component={RouterLink}
                            to={`/admin/user-tools/users?personId=${personId}&tab=1`}
                            sx={{
                              color: theme => theme.palette.primary.main,
                              textDecoration: 'underline'
                            }}
                          >
                            Click here
                          </Link>{' '}
                          to access their details.
                        </Typography>
                      )}
                      {existingUserId && accountIsDisabled && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          This account is disabled, please contact{' '}
                          <a
                            href="mailto:Helpdesk@stonemartinbuilders.com"
                            style={{
                              color: 'inherit',
                              textDecoration: 'underline'
                            }}
                          >
                            Helpdesk@stonemartinbuilders.com
                          </a>{' '}
                          if you believe this is in error.
                        </Typography>
                      )}
                    </Grid>
                    {!isCreating && values.isEmployee === 'Yes' && (
                      <>
                        {!isLoadingSageEmployees && (
                          <Grid item xs={12} sm={4}>
                            <SMBSelect
                              variant="outlined"
                              label="Sage Employee Record"
                              value={values.sageEmployeeId}
                              onChange={event => {
                                setFieldValue('sageEmployeeId', event.target.value);
                              }}
                              handleClear={() => {
                                setFieldValue('sageEmployeeId', '');
                              }}
                              disabled={isLoadingSageEmployees}
                            >
                              {sageEmployees &&
                                sageEmployees.map((sageEmployee, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={sageEmployee.value}>
                                      {sageEmployee.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Grid>
                        )}
                        {hasJobTitleWithTerritories && (
                          <Grid item xs={12} sm={4}>
                            <Autocomplete
                              multiple
                              disabled={isLoadingTerritories}
                              loading={isLoadingTerritories}
                              id="territoryIds"
                              value={selectedTerritoryIds}
                              onChange={(event, newValue: any[]) => {
                                setSelectedTerritoryIds(newValue);
                                setFieldValue(
                                  'territoryIds',
                                  newValue.map(v => v.value)
                                );
                              }}
                              inputValue={territoryInputValue}
                              onInputChange={(event, newInputValue) => {
                                setTerritoryInputValue(newInputValue);
                              }}
                              options={territoriesData ?? []}
                              getOptionLabel={option => option?.description}
                              filterSelectedOptions
                              renderOption={(props, option: IDropdownResponse) => {
                                return (
                                  <li {...props} key={`${option?.value}`}>
                                    {option?.description}
                                  </li>
                                );
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  name="territoryIds"
                                  size="small"
                                  disabled={isLoadingTerritories}
                                  label={isLoadingTerritories ? 'Loading...' : 'Territories'}
                                  placeholder="Add Territory"
                                />
                              )}
                            />
                          </Grid>
                        )}
                        {hasJobTitleWithNeighborhoods && (
                          <Grid item xs={12} sm={4}>
                            <Autocomplete
                              multiple
                              disabled={isLoadingNeighborhoods}
                              loading={isLoadingNeighborhoods}
                              id="neighborhoodIds"
                              value={selectedNeighborhoodIds}
                              onChange={(event, newValue: any[]) => {
                                setSelectedNeighborhoodIds(newValue);
                                setFieldValue(
                                  'neighborhoodIds',
                                  newValue.map(v => v.value)
                                );
                              }}
                              inputValue={neighborhoodInputValue}
                              onInputChange={(event, newInputValue) => {
                                setNeighborhoodInputValue(newInputValue);
                              }}
                              options={neighborhoodsData ?? []}
                              getOptionLabel={option => option?.description}
                              filterSelectedOptions
                              renderOption={(props, option: IDropdownResponse) => {
                                return (
                                  <li {...props} key={`${option?.value}`}>
                                    {option.description}
                                  </li>
                                );
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  name="neighborhoodIds"
                                  size="small"
                                  disabled={isLoadingNeighborhoods}
                                  label={isLoadingAppRoles ? 'Loading...' : 'Neighborhoods'}
                                  placeholder="Add Neighborhood"
                                />
                              )}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    {!isCreating && values.isEmployee === 'No' && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required={isPhoneNumberRequired}
                            variant="outlined"
                            autoComplete="nope"
                            label="Phone Number"
                            name="phone"
                            value={values.phoneNumber}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => {
                              setFieldValue('phoneNumber', formatInputPhoneNumber(e.target.value));
                            }}
                            error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                            helperText={touched.phoneNumber && errors.phoneNumber}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Cell Phone Number"
                            name="phone"
                            value={values.cellPhoneNumber}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => {
                              setFieldValue(
                                'cellPhoneNumber',
                                formatInputPhoneNumber(e.target.value)
                              );
                            }}
                            error={Boolean(touched.cellPhoneNumber && errors.cellPhoneNumber)}
                            helperText={touched.cellPhoneNumber && errors.cellPhoneNumber}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Fax"
                            name="phone"
                            value={values.fax}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => {
                              setFieldValue('fax', formatInputPhoneNumber(e.target.value));
                            }}
                            error={Boolean(touched.fax && errors.fax)}
                            helperText={touched.fax && errors.fax}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Address Line 1"
                            name="address"
                            inputProps={{
                              'data-lpignore': 'true'
                            }}
                            value={values.address}
                            onBlur={handleBlur}
                            size="small"
                            error={touched.address && errors && errors.address ? true : false}
                            helperText={touched.address && errors && errors.address}
                            onChange={e => setFieldValue('address', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Address Line 2"
                            name="address2"
                            inputProps={{
                              'data-lpignore': 'true'
                            }}
                            value={values.address2}
                            onBlur={handleBlur}
                            size="small"
                            error={touched.address2 && errors && errors.address2 ? true : false}
                            helperText={touched.address2 && errors && errors.address2}
                            onChange={e => setFieldValue('address2', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="City"
                            name="city"
                            inputProps={{
                              'data-lpignore': 'true'
                            }}
                            value={values.city}
                            onBlur={handleBlur}
                            size="small"
                            error={touched.city && errors && errors.city ? true : false}
                            helperText={touched.city && errors && errors.city}
                            onChange={e => setFieldValue('city', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <StatesDropdown
                            value={values.state}
                            handleChange={value => {
                              setFieldValue('state', value as string);
                            }}
                            isAdminPortal={true}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="ZIP"
                            name="zip"
                            inputProps={{
                              'data-lpignore': 'true'
                            }}
                            value={values.zip}
                            onBlur={handleBlur}
                            size="small"
                            error={touched.zip && errors && errors.zip ? true : false}
                            helperText={touched.zip && errors && errors.zip}
                            onChange={e => setFieldValue('zip', e.target.value)}
                          />
                        </Grid>
                      </>
                    )}

                    {!isCreating && values.jobTitlePersonType === ExternalUserRoles.Attorney && (
                      <Grid item xs={12} sm={6}>
                        <SelectAsync
                          name="attorneyOfficeId"
                          required
                          label="Attorney Office"
                          id="attorneyOfficeId"
                          apiRequest={() => getAdminLookupAttorneyOffices()}
                          transformResponse={(res: IDropdownResponse[]) =>
                            res.map(r => ({
                              label: r.description,
                              value: r.value
                            }))
                          }
                          value={values.attorneyOfficeId}
                          onChange={({ target: { value } }) => {
                            setFieldValue('attorneyOfficeId', value as string);
                          }}
                        />
                      </Grid>
                    )}
                    {!isCreating &&
                      values.jobTitlePersonType === ExternalUserRoles.InternalAgent && (
                        <AgentFields
                          values={values}
                          setFieldValue={setFieldValue}
                          touched={touched}
                          errors={errors}
                          handleBlur={handleBlur}
                          isFormDisabled={false}
                          currentExternalPersonId={currentUser?.personId}
                          showContactInfo={false}
                        />
                      )}
                    {!isCreating && values.jobTitlePersonType === ExternalUserRoles.Lender && (
                      <LenderFields
                        values={values}
                        isFormDisabled={false}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        touched={touched}
                        errors={errors}
                        currentExternalPersonId={currentUser?.personId}
                        showContactInfo={false}
                      />
                    )}
                    {!isCreating &&
                      values.jobTitlePersonType === ExternalUserRoles.LenderProcessor && (
                        <LenderProcessorFields
                          values={values}
                          isFormDisabled={false}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          touched={touched}
                          errors={errors}
                          currentExternalPersonId={currentUser?.personId}
                          initialValues={initialValues}
                          showContactInfo={false}
                        />
                      )}
                    {!isCreating &&
                      values.jobTitlePersonType === ExternalUserRoles.VendorAffiliate && (
                        <VendorAffiliateFields
                          values={values}
                          isFormDisabled={false}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          touched={touched}
                          errors={errors}
                          showContactInfo={false}
                        />
                      )}
                    {!isCreating && values.jobTitlePersonType === ExternalUserRoles.Purchaser && (
                      <ClientFields
                        values={values}
                        setFieldValue={setFieldValue}
                        touched={touched}
                        errors={errors}
                        handleBlur={handleBlur}
                        isFormDisabled={false}
                        currentExternalPersonId={''}
                        showContactInfo={false}
                        showAdditionalFields
                      />
                    )}
                    {!isCreating && values.jobTitlePersonType === ExternalUserRoles.Custom && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          onChange={e => setFieldValue('workplace', e.target.value)}
                          fullWidth={true}
                          id="workplace"
                          label="Workplace"
                          size="small"
                          required={true}
                          autoComplete="off"
                          value={values.workplace}
                          inputProps={{
                            'data-lpignore': true // Last Pass disable auto-fill
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {showAppRolesInfo && !isMobile && (
                  <UserAppRolesDescriptionDrawer
                    hasCertainHeight={values.jobTitlePersonType === ExternalUserRoles.InternalAgent}
                  />
                )}
              </Grid>
            </Box>
            <Box mt={1} mb={0.5}>
              <Divider />
            </Box>
            <ButtonGroup disableElevation>
              <Button
                disabled={
                  !dirty ||
                  isSubmitting ||
                  !isValid ||
                  (!!existingUserId && currentUser?.email !== values.email) ||
                  !!personId ||
                  (isCreating && !employeeDomainValid)
                }
                type="submit"
                startIcon={<Save />}
                variant="contained"
                color="primary"
              >
                Save
              </Button>
              <Button
                type="button"
                variant="contained"
                color="inherit"
                startIcon={<Close />}
                onClick={() => {
                  history.push('/admin/user-tools/unified-user-management');
                }}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Form>
        </FormikProvider>
      )}
    </>
  );
};
